import { TypoHeading, TypoCaption, TypoCallout, TypoTitle } from '@/components/Typography.tsx';
import Button from '@/components/Button.tsx';
import BlocksRenderer from '@/components/BlocksRenderer.tsx';
import { isDataProp, DataProp, formatPrice } from '@/utils/helpers.tsx';
import useCheckoutAction from '@/hooks/useCheckoutAction.ts';

// Sized by the nearest `@container` (the packages page), not the viewport.
const PackagesList = ({ list, title = 'Packages' }: PackagesListProps) => (
  <>
    <TypoHeading className="font-medium text-neutral-text-strong ml-4 mb-2 cq-md:ml-10">
      {title}
    </TypoHeading>
    <div className="grid-cols-1 space-y-2">
      {list.map((p) => <Package key={p.productId} {...p} />)}
    </div>
  </>
);

const Package = ({ name, price, description }: PackageOption) => {
  const { checkout, loading } = useCheckoutAction(price?.productPriceId ?? 0);
  const priceColorClass = price?.price ? 'text-info' : 'text-neutral-text-softer';

  return (
    <div
      className="p-2 flex flex-col gap-3 justify-between border-2 border-shade-10 rounded-lg cq-md:flex-row cq-md:gap-0"
    >
      <div className="flex flex-col p-4 cq-md:flex-1 cq-md:pl-6">
        <TypoHeading className="font-medium text-neutral-text-strong mb-4">
          {name}
        </TypoHeading>
        {isDataProp(description) && (
          <BlocksRenderer
            data={description}
            config={{
              paragraph: {
                className: 'text-neutral-text-soft font-medium',
                actionsClassNames: {
                  alignment: 'text-{alignment}',
                },
              },
            }} />
        )}
      </div>
      <div className={`
        flex flex-col items-center justify-center w-full
        p-2.5 rounded-lg bg-neutral gap-3 cq-md:w-auto cq-md:min-w-44 cq-md:min-h-56`}>
        <div className="flex items-end gap-1">
          <TypoTitle variant="secondary" className={`font-bold ${priceColorClass}`}>
            {formatPrice(price?.price)}
          </TypoTitle>
          <TypoCallout variant="secondary" className="text-neutral-text-weak font-bold">USD</TypoCallout>
        </div>
        <Button color="success" onClick={() => checkout()} disabled={loading}>
          Get Access
        </Button>
        <TypoCaption className="font-semibold">Pay once, own it forever</TypoCaption>
      </div>
    </div>
  );
};

export interface PackageOption {
  productId: number;
  name: string;
  description: DataProp | null;
  price: {
    productPriceId: number;
    price: number;
  } | null;
}

interface PackagesListProps {
  list: Array<PackageOption>;
  title?: string;
}

export default PackagesList;
